import { useState, useEffect, useRef, Fragment } from "react";
import {
  CardHeader,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Loading,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  Autocomplete,
  CircularProgress,
  Typography,
  Divider,
  TextareaAutosize,
} from "../../../../components";
import {
  notification,
  ConfiguracoesOrdemServicoService,
  PlanosFinanceirosService,
} from "../../../../services";
import { InputErros, value } from "../../../../helpers";
import { responseErros, userLogs } from "../../../../utils";

const ConfiguracoesOrdemServicosDialog = ({
  handleCloseDialog,
  openDialog,
}) => {
  const [loading, setLoading] = useState(false);
  const [configuracoes, setConfiguracoes] = useState({
    movimentacaoEstoque: false,
    movimentacaoFinanceira: false,
    serieDocumento: "OS",
  });
  const inputPlanoFinanceiroServico = useRef();
  const inputPlanoFinanceiroProduto = useRef();
  const [listaPlanosFinanceirosServicos, setListaPlanosFinanceirosServicos] =
    useState([]);
  const [listaPlanosFinanceirosProdutos, setListaPlanosFinanceirosProdutos] =
    useState([]);
  const [loadingAutoCompletePlanoProduto, setLoadingAutoCompletePlanoProduto] =
    useState(false);
  const [loadingAutoCompletePlanoServico, setLoadingAutoCompletePlanoServico] =
    useState(false);
  const [userLog, setUserLog] = useState({});
  const [inputErro, setInputErro] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);

  const planosFinanceiroService = new PlanosFinanceirosService();
  const configuracoesService = new ConfiguracoesOrdemServicoService();

  useEffect(() => {
    if (openDialog) {
      buscaConfiguracoes();
    }
  }, [openDialog]);

  const buscaConfiguracoes = async () => {
    setLoading(true);
    const { data } = await configuracoesService.getAll();
    setConfiguracoes(data?.[0]);

    const planoServicosId = data?.[0]?.planoFinanceiroServicosId,
      planoProdutosId = data?.[0]?.planoFinanceiroProdutosId;
    if (planoServicosId) {
      buscarPlanosFinanceiros({ id: [planoServicosId] }, "servico");
    }
    if (planoProdutosId) {
      buscarPlanosFinanceiros({ id: [planoProdutosId] }, "produto");
    }
    setLoading(false);
  };

  const buscarPlanosFinanceiros = async (filtros, classificacao) => {
    const res = await planosFinanceiroService.getAll(filtros);
    if (!res.isAxiosError) {
      if (classificacao === "produto") {
        setListaPlanosFinanceirosProdutos(res.data.rows);
      } else {
        setListaPlanosFinanceirosServicos(res.data.rows);
      }
    } else {
      responseErros(res);
    }

    if (classificacao === "produto") {
      setLoadingAutoCompletePlanoProduto(false);
    } else {
      setLoadingAutoCompletePlanoServico(false);
    }
  };

  const onSearchChangePlanoFinanceiro = (event, classificacao) => {
    if (
      event.key === "Enter" &&
      (inputPlanoFinanceiroServico.current === document.activeElement ||
        inputPlanoFinanceiroProduto.current === document.activeElement)
    ) {
      const eventValue = event.target?.value;
      let filtros = {
        descricao: eventValue,
        ativado: true,
      };
      if (classificacao === "produto") {
        setLoadingAutoCompletePlanoProduto(true);
      } else {
        setLoadingAutoCompletePlanoServico(true);
      }
      buscarPlanosFinanceiros(filtros, classificacao);
    }
  };

  const handleInputChange = (e) => {
    e.persist();
    setConfiguracoes((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const onChangeAutocomplete = (name, value) => {
    setConfiguracoes((prev) => ({
      ...prev,
      [name]: value ? value.id : undefined,
    }));
  };

  const handleCheckboxChange = (name) => {
    setConfiguracoes((prev) => ({
      ...prev,
      [name]: !prev?.[name],
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    if (!configuracoes?.serieDocumento?.trim()) {
      setInputErro(["serieDocumento"]);
      setLoading(false);
      return notification.alertaGenericos("Serie do documento é obrigatório");
    }
    const id = configuracoes?.id;
    let response;
    if (id) {
      response = await configuracoesService.atualizar(id, configuracoes);
    } else {
      response = await configuracoesService.cadastrar(configuracoes);
    }

    setLoading(false);
    setInputErro([]);
    if (!response.isAxiosError) {
      id ? notification.alteracaoSucesso() : notification.cadastroSucesso();
      handleCloseDialog();
    } else {
      responseErros(response);
    }
  };

  async function onClickLog() {
    if ("userCreatedLog" in userLog) return;
    const logs = await userLogs(configuracoes);
    setUserLog(logs);
  }

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={openDialog}
      onClose={handleCloseDialog}
    >
      <CardHeader
        title="Configurações da Ordem de Serviço"
        style={{ textAlign: "center" }}
        userLog={configuracoes?.id ? userLog : null}
        onClickLog={onClickLog}
      />
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Classificação Financeira
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2}>
            <TextField
              id="serieDocumento"
              name="serieDocumento"
              label="Série Documento"
              variant="outlined"
              value={
                configuracoes?.serieDocumento
                  ? configuracoes.serieDocumento
                  : ""
              }
              onBlur={(e) => {
                const value = e.target.value === "" ? "OS" : e.target.value;
                setConfiguracoes((prev) => ({
                  ...prev,
                  [e.target.name]: value,
                }));
              }}
              onChange={handleInputChange}
              fullWidth
              InputProps={{
                inputProps: {
                  maxLength: 3,
                  style: { textTransform: "uppercase" },
                },
              }}
              required
              error={inputErros.get("serieDocumento")}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <FormControl variant="outlined" fullWidth>
              <Autocomplete
                id="planoFinanceiroServicosId"
                name="planoFinanceiroServicosId"
                options={listaPlanosFinanceirosServicos}
                noOptionsText="Digite e pressione Enter"
                autoHighlight
                getOptionLabel={(option) =>
                  option.descricao
                    ? `${option.id}` + ` - ` + `${option.descricao}`
                    : ""
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Plano Financeiro - Serviços"
                    variant="outlined"
                    inputRef={inputPlanoFinanceiroServico}
                    onKeyDown={(e) =>
                      onSearchChangePlanoFinanceiro(e, "servico")
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          <Fragment>
                            {loadingAutoCompletePlanoServico ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </Fragment>
                        </>
                      ),
                    }}
                  />
                )}
                onChange={(e, value) =>
                  onChangeAutocomplete("planoFinanceiroServicosId", value)
                }
                value={value.autoComplete(
                  listaPlanosFinanceirosServicos,
                  configuracoes?.planoFinanceiroServicosId,
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            <FormControl variant="outlined" fullWidth>
              <Autocomplete
                id="planoFinanceiroProdutosId"
                name="planoFinanceiroProdutosId"
                options={listaPlanosFinanceirosProdutos}
                noOptionsText="Digite e pressione Enter"
                autoHighlight
                getOptionLabel={(option) =>
                  option.descricao
                    ? `${option.id}` + ` - ` + `${option.descricao}`
                    : ""
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Plano Financeiro - Produtos"
                    variant="outlined"
                    inputRef={inputPlanoFinanceiroProduto}
                    onKeyDown={(e) =>
                      onSearchChangePlanoFinanceiro(e, "produto")
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          <Fragment>
                            {loadingAutoCompletePlanoProduto ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </Fragment>
                        </>
                      ),
                    }}
                  />
                )}
                onChange={(e, value) =>
                  onChangeAutocomplete("planoFinanceiroProdutosId", value)
                }
                value={value.autoComplete(
                  listaPlanosFinanceirosProdutos,
                  configuracoes?.planoFinanceiroProdutosId,
                )}
              />
            </FormControl>
          </Grid>
        </Grid>
        <Divider sx={{ my: 3 }} />
        <Typography variant="body2" sx={{ mb: 2 }}>
          Informações Fiscais
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              id="serieNfse"
              name="serieNfse"
              label="Série NFS-e"
              variant="outlined"
              value={configuracoes?.serieNfse ? configuracoes.serieNfse : ""}
              onChange={handleInputChange}
              fullWidth
              error={inputErros.get("serieNfse")}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              id="sequenciaNfse"
              name="sequenciaNfse"
              label="Sequência NFS-e"
              variant="outlined"
              value={
                configuracoes?.sequenciaNfse ? configuracoes.sequenciaNfse : ""
              }
              onChange={handleInputChange}
              fullWidth
              error={inputErros.get("sequenciaNfse")}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={configuracoes?.movimentacaoEstoque || false}
                  onChange={() => handleCheckboxChange("movimentacaoEstoque")}
                  color="primary"
                />
              }
              label="Movimentação de Estoque"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={configuracoes?.movimentacaoFinanceira || false}
                  onChange={() =>
                    handleCheckboxChange("movimentacaoFinanceira")
                  }
                  color="primary"
                />
              }
              label="Movimentação Financeira"
            />
          </Grid>
        </Grid>
        <Divider sx={{ my: 3 }} />
        <Typography variant="body2" sx={{ mb: 2 }}>
          Descrição do Serviço Fixa
        </Typography>
        <TextareaAutosize
          id="descricaoServicoFixa"
          name="descricaoServicoFixa"
          className="textRolagem bg-transparent"
          maxLength={550}
          value={configuracoes?.descricaoServicoFixa || ""}
          onChange={handleInputChange}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={configuracoes?.permiteEditarDescricaoServico || false}
              onChange={() =>
                handleCheckboxChange("permiteEditarDescricaoServico")
              }
              color="primary"
            />
          }
          label="Permitir editar na O.S"
        />
      </DialogContent>
      <DialogActions className="px-3 m-3">
        <Button
          id="botaoSalvar"
          color="primary"
          variant="contained"
          onClick={handleSubmit}
        >
          <i
            className="ph-fill ph-check-circle"
            style={{ fontSize: 22, marginRight: 10 }}
          ></i>
          Salvar
        </Button>
        <Button color="inherit" variant="contained" onClick={handleCloseDialog}>
          <i
            className="ph-fill ph-x-circle"
            style={{ fontSize: 22, marginRight: 10 }}
          ></i>
          Fechar
        </Button>
      </DialogActions>
      <Loading loading={loading} />
    </Dialog>
  );
};

export default ConfiguracoesOrdemServicosDialog;
